import {
  MarketingTriggerKey,
  OrderTrackingTriggerKey,
} from "@redotech/redo-flows-common/triggers";
import * as z from "zod";
import { SkillId } from "./skill-id";

/**
 * Playbook UI taxonomy. Each trigger belongs to exactly one category, which
 * drives how it's grouped in the merchant-facing playbook tree. Unrelated to
 * routing — see TriggerConversationType for that.
 */
export enum TriggerCategory {
  CART_CHECKOUT = "cart_checkout",
  SIGNUPS = "signups",
  PRODUCT_INTEREST = "product_interest",
  RETURNS_WARRANTY = "returns_warranty",
  ORDER_LIFECYCLE = "order_lifecycle",
  SHIPPING_UPDATES = "shipping_updates",
  SHIPPING_ISSUES = "shipping_issues",
  PICKUP = "pickup",
}

/**
 * Categories that happen after a purchase — used by the Text Concierge product
 * and landing page to scope "post-purchase" experiences. Pre-purchase is the
 * complement.
 */
export const POST_PURCHASE_CATEGORIES: readonly TriggerCategory[] = [
  TriggerCategory.RETURNS_WARRANTY,
  TriggerCategory.ORDER_LIFECYCLE,
  TriggerCategory.SHIPPING_UPDATES,
  TriggerCategory.SHIPPING_ISSUES,
  TriggerCategory.PICKUP,
];

export const PRE_PURCHASE_CATEGORIES: readonly TriggerCategory[] = [
  TriggerCategory.CART_CHECKOUT,
  TriggerCategory.SIGNUPS,
  TriggerCategory.PRODUCT_INTEREST,
];

/**
 * Which product handles conversations started by this trigger.
 * - CONCIERGE: advanced-flow concierge step — reactive, conversational support.
 * - MARKETING: vanguard loop + marketing-agent coordinator — proactive outreach.
 *
 * String values mirror MarketingConversationType in redo-marketing-common so
 * the temporal activity can cast at the package boundary without a lookup.
 */
export enum TriggerConversationType {
  CONCIERGE = "CONCIERGE",
  MARKETING = "MARKETING",
}

/** Analytics discriminator on shopper_agent_insights; uses the current product name for the reactive category. */
export enum ConversationInsightType {
  COMMERCE_AGENT = "COMMERCE_AGENT",
  MARKETING = "MARKETING",
}

/** Maps the routing category to its analytics label; the legacy CONCIERGE routing value is Commerce Agent today. */
export function conversationInsightTypeFromTrigger(
  type: TriggerConversationType,
): ConversationInsightType {
  return type === TriggerConversationType.MARKETING
    ? ConversationInsightType.MARKETING
    : ConversationInsightType.COMMERCE_AGENT;
}

export const TriggerKeySchema = z.enum([
  ...Object.values(MarketingTriggerKey),
  ...Object.values(OrderTrackingTriggerKey),
]);

export type TriggerKey = z.infer<typeof TriggerKeySchema>;

export const TriggerDefinitionSchema = z.object({
  id: TriggerKeySchema,
  name: z.string(),
  description: z.string(),
  /**
   * Timing bucket — used for UI grouping in the playbook (pre- vs
   * post-purchase). Every defined trigger has one; triggers that intentionally
   * have no playbook surface (e.g. INBOUND_RECEIVED) are listed in
   * UNSUPPORTED_TRIGGERS and have no TriggerDefinition at all.
   */
  category: z.enum(TriggerCategory),
  /** Routing decision — which product handles conversations from this trigger. */
  conversationType: z.enum(TriggerConversationType),
  defaultEnabled: z.boolean(),
  /**
   * Whether merchants can attach advanced-flow automations to this trigger
   * in the playbook UI. When false:
   *   - the UI hides the automations selector and shows an explainer banner;
   *   - the RPC rejects writes to `automations`;
   *   - the routing helper ignores any persisted `automations` entries.
   * Today the only `false` case is SMS_SIGNUP, which bypasses the shopper
   * agent entirely for regulatory reasons (welcome flows must always fire).
   * Defaults to true when omitted.
   */
  supportsAutomations: z.boolean().optional(),
  /**
   * Whether merchants can configure per-trigger skip conditions in the
   * playbook UI. When false:
   *   - the UI hides the skip-conditions builder and shows an explainer;
   *   - the RPC rejects writes to `skipConditions`;
   *   - the runtime evaluator never consults persisted conditions for
   *     this trigger (defense in depth — the gate also short-circuits
   *     when no conditions exist).
   * Today the only `false` case is SMS_SIGNUP, where SMS regulation
   * requires the welcome flow to always fire and the coordinator handoff
   * happens in MarketingConfirmedHandler with no SchemaInstance available
   * to evaluate against. Defaults to true when omitted.
   */
  supportsSkipConditions: z.boolean().optional(),
  /** Skills this trigger can invoke */
  skills: z.array(z.enum(SkillId)),
  /** Prompt guidance injected when this trigger fires — gives the agent context about the event */
  guidance: z.string().optional(),
  /**
   * Internal coordinator behavior for this trigger. `defaultBypass` controls
   * the trigger-definition default; `supportsOverride` controls whether a
   * merchant/team trigger config may override that default.
   */
  processingPolicy: z
    .object({
      buffer: z
        .object({
          defaultBypass: z.boolean().optional(),
          supportsOverride: z.boolean().optional(),
        })
        .optional(),
      sentinel: z
        .object({
          defaultBypass: z.boolean().optional(),
          supportsOverride: z.boolean().optional(),
        })
        .optional(),
    })
    .optional(),
});

export type TriggerDefinition = z.infer<typeof TriggerDefinitionSchema>;
